import React, { FC, useState, useEffect, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'
import { maxLength } from '../../../utils/validate'
import { useTranslation } from 'react-i18next'
import { replaceAndStrip } from '../../../utils/string'
import Modal from '../common/Modal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AddMemberModule from './AddMemberModule'
import { sortBy } from 'lodash'
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import { arrayMove, SortableContext, rectSortingStrategy } from '@dnd-kit/sortable'
import Grid from './Grid'
import SortableItem from './SortableItem'
import { compile } from '../../../utils/search'

interface MemberDetail {
  id: number
  name: string
  kana: string | null
  profile_image: string | null
}

interface TeamDetail {
  code: string | null
  id: number
  name: string
  parent_team_id: number | null
  managers: MemberDetail[]
  members: MemberDetail[]
}

interface Errors {
  name: string[]
  flow: string[]
  member: string[]
}

interface Flow extends MemberDetail {
  priority: number
  item_id: number
  team_name?: string
  team_manager_ids?: number[]
}

interface ApprovalGroup {
  id: number
  name: string
  description: string | null
  created_by: string | null
  approve_members: {
    id: number
    priority: number
    name: string
    kana: string | null
    profile_image: string | null
  }[]
  request_members: MemberDetail[]
  viewable_members: MemberDetail[]
}

interface ModalState {
  display: boolean,
  type: string,
  id: number | undefined,
  targetData: ApprovalGroup | null
  memberList: MemberDetail[]
  teamList: TeamDetail[]
  alreadyAssignedMemberIds: number[]
}

interface CreateParams {
  name: string
  description: string | null
  approve_members: {
    member_id: number
    priority: number
  }[]
  request_member_ids: number[]
  viewable_member_ids: number[]
}

interface UpdateParams {
  id: number
  name?: string
  description?: string | null
  approve_members?: {
    member_id: number
    priority: number
  }[]
  request_member_ids?: number[]
  viewable_member_ids?: number[]
}

interface Props {
  modalState: ModalState
  closeCallback: VoidFunction
  createCallback: (params: CreateParams, diff: number[]) => void
  updateCallback: (params: UpdateParams, diff: number[]) => void
}

const ApproverSettingModal: FC<Props> = (props) => {
  const { t } = useTranslation()

  const {
    commonPart,
  } = useSelector((state: RootState) => state)

  const {
    modalState,
    closeCallback,
    createCallback,
    updateCallback,
  } = props

  const initialInput = {
    name: "",
    description: null,
    approvers: [],
    members: [],
  }

  const initErrors = {
    name: [],
    flow: [],
    member: [],
  }

  const initFlow: Flow = {
    priority: 0,
    item_id: 0,
    id: 0,
    name: "",
    kana: null,
    profile_image: null,
  }

  const [groupName, setGroupName] = useState<string>("")

  const [displayModal, setDisplayModal] = useState<boolean>(false)
  const [errors, setErrors] = useState<Errors>(initErrors)

  const [selectRequestMembers, setSelectRequestMembers] = useState<MemberDetail[]>([])
  const [otherAssignedMembers, setOtherAssignedMembers] = useState<MemberDetail[]>([])
  const [flow, setFlow] = useState<Flow[]>([])

  const [selectViewableMembers, setSelectViewableMembers] = useState<MemberDetail[]>([])

  const [isMoving, setIsMoving] = useState<boolean>(true)

  const [displaySelectBoxNum, setDisplaySelectBoxNum] = useState<number | null>(null)

  const [searchMemberWord, setSearchMemberWord] = useState<string>("")

  const [approverMembers, setApproverMembers] = useState<string>("")

  const [isFreeBuild, setIsFreeBuild] = useState<boolean>(true)

  const [selectTeamId, setSelectTeamId] = useState<number>(0)

  const filterMemberList = useMemo(() => {
    if (!searchMemberWord) return modalState.memberList
    const searchWordArray = compile(searchMemberWord.toLocaleLowerCase().split(/\s+/))
    return modalState.memberList.filter((v) => searchWordArray.test(v.name.toLocaleLowerCase() + v.kana))
  }, [searchMemberWord, modalState.memberList])

  const sortByHierarchy = (items: TeamDetail[]): TeamDetail[] => {
    const childrenMap = new Map<number | null, TeamDetail[]>()
    items.forEach((item) => {
      const parentId = item.parent_team_id
      if (!childrenMap.has(parentId)) {
        childrenMap.set(parentId, [])
      }
      childrenMap.get(parentId)?.push(item)
    })
    const result: TeamDetail[] = []
    const traverse = (parentId: number | null) => {
      const children = childrenMap.get(parentId) || []
      children.forEach((child) => {
        result.push(child)
        traverse(child.id)
      })
    }
    traverse(null)
    return result
  }

  const sortedTeamList = useMemo(() => (
    sortByHierarchy(modalState.teamList)
  ), [modalState.teamList])

  const getParentTeamNameList = (id: number | null): TeamDetail[] => {
    if (id === null) return []
    const targetTeam = sortedTeamList.find((v) => v.id === id)
    if (!targetTeam || !targetTeam.parent_team_id) return []
    return [targetTeam, ...getParentTeamNameList(targetTeam.parent_team_id)]
  }

  const buildFullPathName = (id: number) => getParentTeamNameList(id).map((v) => v.name).join(" > ")

  const clear = () => {
    closeCallback()
    setTimeout(() => {
      setGroupName("")
      setSelectRequestMembers([])
      setFlow([])
      setSearchMemberWord("")
    }, 500)
  }

  const replaceAndStripName = (event: React.FocusEvent<HTMLInputElement>) => {
    setGroupName(replaceAndStrip(event.target.value))
    if (event.target.value) {
      setErrors({
        ...errors,
        name: [],
      })
    }
  }

  const setRequestMember = (members: MemberDetail[]) => {
    setSelectRequestMembers(members)
  }

  const delRequestMember = (memberId: number) => {
    setSelectRequestMembers((cur) => cur.filter((v) => v.id !== memberId))
  }

  const setViewableMember = (members: MemberDetail[]) => {
    setSelectViewableMembers(members)
  }

  const delViewableMember = (memberId: number) => {
    setSelectViewableMembers((cur) => cur.filter((v) => v.id !== memberId))
  }

  const numArrayDiff = (arr1: number[], arr2: number[]) => {
    const diff1 = arr1.filter((v) => arr2.indexOf(v) === -1)
    const diff2 = arr2.filter((v) => arr1.indexOf(v) === -1)
    return [...diff1, ...diff2]
  }

  const diffApprovers = () => {
    const originApprovers = modalState.targetData?.approve_members || []
    const diffApproverMembers = numArrayDiff(flow.map((v) => v.id), originApprovers.map((v) => v.id))
    if (diffApproverMembers.length) return true
    return flow.some((v) => originApprovers.some((w) => v.id === w.id && v.priority !== w.priority))
  }

  const saveApprovalGroup = () => {
    if (!commonPart.company?.id) return
    const tmpErrors: Errors = initErrors
    let errorFlg = false

    // name
    if (groupName === '') {
      tmpErrors.name.push("承認グループ名を入力してください。")
      errorFlg = true
    }
    const nameLength = maxLength(30)(groupName)
    if (nameLength) {
      tmpErrors.name.push(t(nameLength, { length: 30 }))
      errorFlg = true
    }
    setErrors({
      name: tmpErrors.name,
      flow: tmpErrors.flow,
      member: tmpErrors.member,
    })
    if (errorFlg) return
    if (modalState.type === "create") {
      const tmpValues: CreateParams = {
        name: groupName,
        description: null,
        approve_members: flow.filter((v) => !!v.id).map((v, i, self) => ({ member_id: v.id, priority: self.length - (i + 1) })),
        request_member_ids: selectRequestMembers.map((v) => v.id),
        viewable_member_ids: selectViewableMembers.map((v) => v.id),
      }
      createCallback(tmpValues, otherAssignedMembers.map((v) => v.id))
    } else if (modalState.type === "update" && modalState.id) {
      let tmpValues:UpdateParams = {
        id: modalState.id,
      }
      if (diffApprovers()) {
        tmpValues = {
          ...tmpValues,
          approve_members: flow.filter((v) => !!v.id).map((v, i, self) => ({ member_id: v.id, priority: self.length - (i + 1) })),
        }
      }
      const diffRequestMembers = numArrayDiff(selectRequestMembers.map((v) => v.id), modalState.targetData?.request_members.map((w) => w.id) || [])
      if (diffRequestMembers.length) {
        tmpValues = {
          ...tmpValues,
          request_member_ids: selectRequestMembers.map((v) => v.id),
        }
      }
      const diffViewableMembers = numArrayDiff(selectViewableMembers.map((v) => v.id), modalState.targetData?.viewable_members.map((w) => w.id) || [])
      if (diffViewableMembers.length) {
        tmpValues = {
          ...tmpValues,
          viewable_member_ids: selectViewableMembers.map((v) => v.id),
        }
      }
      if ((modalState.targetData?.name || initialInput.name) !== groupName) {
        tmpValues = {
          ...tmpValues,
          name: groupName,
        }
      }
      updateCallback(tmpValues, otherAssignedMembers.map((v) => v.id))
    }
  }

  const addFlow = () => {
    let addValues = {
      ...initFlow,
    }
    if (flow.length) {
      addValues = { ...addValues, item_id: Math.max(...flow.map((v) => v.item_id)) + 1 }
    } else {
      addValues = { ...addValues, item_id: 0 }
    }
    setFlow([
      ...flow.map((v) => ({ ...v, priority: v.priority + 1 })),
      addValues,
    ])
  }

  const delFlow = (itemId: number) => {
    const tmpLength = flow.length - 2
    setFlow(flow.filter((v) => v.item_id !== itemId).map((v, i) => ({ ...v, priority: tmpLength - i })))
  }

  const closeApproverList = () => {
    setDisplaySelectBoxNum(null)
    setSearchMemberWord("")
  }

  const changeFlowType = (isFree: boolean) => {
    if (isFree) {
      setIsFreeBuild(true)
    } else {
      setIsFreeBuild(false)
      const tmpList = getParentTeamNameList(selectTeamId)
      setFlow(
        tmpList.map((v, i) => ({
          ...initFlow,
          item_id: i,
          priority: tmpList.length - (i + 1),
          team_name: v.name,
          team_manager_ids: v.managers.map((w) => w.id),
        })),
      )
      setSelectRequestMembers(modalState.teamList.find((v) => v.id === selectTeamId)?.members || [])
    }
  }

  const selectTargetTeamId = (teamId: number) => {
    setSelectTeamId(teamId)
    const tmpList = getParentTeamNameList(teamId)
    setFlow(
      tmpList.map((v, i) => ({
        ...initFlow,
        item_id: i,
        priority: tmpList.length - (i + 1),
        team_name: v.name,
        team_manager_ids: v.managers.map((w) => w.id),
      })),
    )
    setSelectRequestMembers(modalState.teamList.find((v) => v.id === teamId)?.members || [])
  }

  const selectApprover = (selectMemberId: number, itemId: number) => {
    const selectMember = modalState.memberList.find((v) => v.id === selectMemberId)
    if (!selectMember) return
    setFlow(
      flow.map((v) => (v.item_id === itemId
        ? { ...v, id: selectMember.id, name: selectMember.name, kana: selectMember.kana, profile_image: selectMember.profile_image }
        : v
      )),
    )
    closeApproverList()
  }

  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor))

  const handleDragStart = useCallback(() => {
    setIsMoving(false)
  }, [])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    if (active.id !== over?.id) {
      setFlow((values) => {
        const oldIndex = values.map((v) => v.item_id).indexOf(Number(active.id))
        const newIndex = values.map((v) => v.item_id).indexOf(Number(over?.id))

        return arrayMove(values, oldIndex, newIndex).map((v, i) => ({ ...v, priority: values.length - (i + 1) }))
      })
    }
    setIsMoving(true)
  }, [])

  const handleDragCancel = useCallback(() => {
    setIsMoving(true)
  }, [])

  const buildApprovalFlow = (list: Flow[]) => {
    if (!list.length) {
      return (
        <>
          承認フローが設定されていません
          {
            isFreeBuild && (
              <button
                type="button"
                style={{
                  fontSize: "20px",
                  padding: 0,
                  color: "#039831",
                }}
                onClick={addFlow}
              >
                <FontAwesomeIcon icon={['fas', 'plus-circle']} />
              </button>
            )
          }
        </>
      )
    }
    return (
      <>
        {
          !isFreeBuild ? (
            <Grid columns={4}>
              {
                list.map((item, i) => (
                  <div
                    key={`approver_${item.item_id}`}
                    className="approver_block_wrapper"
                  >
                    {
                      displaySelectBoxNum === item.item_id && (
                        <>
                          <div
                            style={{
                              backgroundColor: "#FFFFFF",
                              position: "absolute",
                              top: "50px",
                              zIndex: "2005",
                              borderRadius: "4px",
                              padding: "16px",
                              border: "solid 1px #CCCCCC",
                            }}
                          >
                            <div
                              style={{
                                padding: "4px",
                                fontWeight: "bold",
                                textAlign: "center",
                              }}
                            >
                              承認者設定
                            </div>
                            <div>
                              <input
                                type="search"
                                placeholder="検索キーワードを入力"
                                onChange={(e) => setSearchMemberWord(e.target.value)}
                                style={{
                                  border: "solid 1px #CCCCCC",
                                  width: "100%",
                                  padding: "8px",
                                  outline: "unset",
                                  marginBottom: "8px",
                                }}
                              />
                            </div>
                            <div
                              style={{
                                padding: "4px",
                                fontWeight: "bold",
                              }}
                            >
                              {`${item.team_name}のマネージャー`}
                            </div>
                            <div
                              style={{
                                border: "solid 1px #CCCCCC",
                                borderRadius: "4px",
                                width: "350px",
                                maxHeight: "250px",
                                overflowY: "scroll",
                                padding: "8px",
                                display: "flex",
                                flexWrap: "wrap",
                                gap: "4px",
                              }}
                            >
                              {
                                !filterMemberList.filter((v) => item.team_manager_ids?.includes(v.id)).length && (
                                  <>
                                    対象のマネージャーがいません
                                  </>
                                )
                              }
                              {
                                filterMemberList.filter((v) => item.team_manager_ids?.includes(v.id)).map((v) => (
                                  <div
                                    key={`select_${item.item_id}_${v.id}`}
                                    onClick={() => selectApprover(v.id, item.item_id)}
                                    role="none"
                                    style={{
                                      cursor: "pointer",
                                      padding: "4px 8px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "left",
                                      gap: "4px",
                                      border: "solid 1px #CCCCCC",
                                      borderRadius: "50px",
                                      backgroundColor: v.id === item.id ? "#f3fff3" : "#FFFFFF",
                                    }}
                                  >
                                    <img
                                      alt="profile"
                                      src={v.profile_image || `/static/images/profile/${(Number(v.id) || 0) % 12}.jpeg`}
                                      className="profile_icon"
                                      style={{
                                        width: "24px",
                                        height: "24px",
                                        borderRadius: "50%",
                                      }}
                                    />
                                    <ruby
                                      style={{
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {v.name}
                                      <rt>{v.kana}</rt>
                                    </ruby>
                                  </div>
                                ))
                              }
                            </div>
                          </div>
                          <div className="close_screen" role="presentation" onClick={() => closeApproverList()} />
                        </>
                      )
                    }
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <div className="approver_block">
                        <div className="approver_block_title">
                          <div>
                            {list.length === i + 1 ? "最終" : `${i + 1}次`}
                            承認ユーザー
                          </div>
                        </div>
                        <div className="approver_block_main" style={{ flexFlow: "column" }}>
                          <div
                            style={{
                              fontSize: "10px",
                              fontWeight: "bold",
                            }}
                          >
                            {item.team_name}
                          </div>
                          <div
                            onClick={() => setDisplaySelectBoxNum(item.item_id)}
                            role="none"
                            className="approver_select_box_wrapper"
                            style={{
                              position: "relative",
                              transform: "none",
                              width: "100%",
                              top: "unset",
                              left: "unset",
                            }}
                          >
                            {
                              item.id ? (
                                <>
                                  <img
                                    alt="profile"
                                    src={item.profile_image || `/static/images/profile/${(Number(item.id) || 0) % 12}.jpeg`}
                                    className="profile_icon"
                                    style={{
                                      width: "20px",
                                      height: "20px",
                                      borderRadius: "50%",
                                    }}
                                  />
                                  <ruby
                                    style={{
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {item.name}
                                    <rt>{item.kana}</rt>
                                  </ruby>
                                </>
                              ) : (
                                <>
                                  選択してください
                                </>
                              )
                            }
                          </div>
                        </div>
                      </div>
                      {
                        list.length > i + 1 ? (
                          <FontAwesomeIcon icon={['fas', 'arrow-right']} />
                        ) : (
                          <div
                            style={{
                              width: "10px",
                            }}
                          />
                        )
                      }
                    </div>
                  </div>
                ))
              }
            </Grid>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <SortableContext items={list.map((v) => v.item_id.toString())} strategy={rectSortingStrategy}>
                <Grid columns={4}>
                  {
                    list.map((item, i) => (
                      <div
                        key={`approver_${item.item_id}`}
                        className="approver_block_wrapper"
                      >
                        {
                          isMoving && (
                            <>
                              <button
                                type="button"
                                className="delete_approver"
                                onClick={() => delFlow(item.item_id)}
                              >
                                <FontAwesomeIcon icon={['fas', 'times']} />
                              </button>
                              <div
                                onClick={() => setDisplaySelectBoxNum(item.item_id)}
                                role="none"
                                className="approver_select_box_wrapper"
                              >
                                {
                                  item.id ? (
                                    <>
                                      <img
                                        alt="profile"
                                        src={item.profile_image || `/static/images/profile/${(Number(item.id) || 0) % 12}.jpeg`}
                                        className="profile_icon"
                                        style={{
                                          width: "20px",
                                          height: "20px",
                                          borderRadius: "50%",
                                        }}
                                      />
                                      <ruby
                                        style={{
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {item.name}
                                        <rt>{item.kana}</rt>
                                      </ruby>
                                    </>
                                  ) : (
                                    <>
                                      選択してください
                                    </>
                                  )
                                }
                              </div>
                            </>
                          )
                        }
                        {
                          displaySelectBoxNum === item.item_id && (
                            <>
                              <div
                                style={{
                                  backgroundColor: "#FFFFFF",
                                  position: "absolute",
                                  top: "50px",
                                  zIndex: "2005",
                                  borderRadius: "4px",
                                  padding: "16px",
                                  border: "solid 1px #CCCCCC",
                                }}
                              >
                                <div
                                  style={{
                                    padding: "4px",
                                    fontWeight: "bold",
                                    textAlign: "center",
                                  }}
                                >
                                  承認者設定
                                </div>
                                <div>
                                  <input
                                    type="search"
                                    placeholder="検索キーワードを入力"
                                    onChange={(e) => setSearchMemberWord(e.target.value)}
                                    style={{
                                      border: "solid 1px #CCCCCC",
                                      width: "100%",
                                      padding: "8px",
                                      outline: "unset",
                                      marginBottom: "8px",
                                    }}
                                  />
                                </div>
                                <div
                                  style={{
                                    padding: "4px",
                                    fontWeight: "bold",
                                  }}
                                >
                                  ユーザー
                                </div>
                                <div
                                  style={{
                                    border: "solid 1px #CCCCCC",
                                    borderRadius: "4px",
                                    width: "350px",
                                    maxHeight: "250px",
                                    overflowY: "scroll",
                                    padding: "8px",
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "4px",
                                  }}
                                >
                                  {
                                    !filterMemberList.length && (
                                      <>
                                        対象のユーザーがいません
                                      </>
                                    )
                                  }
                                  {
                                    filterMemberList.map((v) => (
                                      <div
                                        key={`select_${item.item_id}_${v.id}`}
                                        onClick={() => selectApprover(v.id, item.item_id)}
                                        role="none"
                                        style={{
                                          cursor: "pointer",
                                          padding: "4px 8px",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "left",
                                          gap: "4px",
                                          border: "solid 1px #CCCCCC",
                                          borderRadius: "50px",
                                          backgroundColor: v.id === item.id ? "#f3fff3" : "#FFFFFF",
                                        }}
                                      >
                                        <img
                                          alt="profile"
                                          src={v.profile_image || `/static/images/profile/${(Number(v.id) || 0) % 12}.jpeg`}
                                          className="profile_icon"
                                          style={{
                                            width: "24px",
                                            height: "24px",
                                            borderRadius: "50%",
                                          }}
                                        />
                                        <ruby
                                          style={{
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {v.name}
                                          <rt>{v.kana}</rt>
                                        </ruby>
                                      </div>
                                    ))
                                  }
                                </div>
                              </div>
                              <div className="close_screen" role="presentation" onClick={() => closeApproverList()} />
                            </>
                          )
                        }
                        <SortableItem id={item.item_id.toString()}>
                          <div className="approver_block">
                            <div className="approver_block_title">
                              <div className="display_icon">
                                <FontAwesomeIcon icon={['fas', 'grip-vertical']} />
                              </div>
                              <div>
                                {list.length === i + 1 ? "最終" : `${i + 1}次`}
                                承認ユーザー
                              </div>
                            </div>
                            <div className="approver_block_main">
                              {
                                isMoving || (
                                  <>
                                    {
                                      item.id ? (
                                        <>
                                          <img
                                            alt="profile"
                                            src={item.profile_image || `/static/images/profile/${(Number(item.id) || 0) % 12}.jpeg`}
                                            className="profile_icon"
                                          />
                                          <ruby
                                            style={{
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            {item.name}
                                            <rt>{item.kana}</rt>
                                          </ruby>
                                        </>
                                      ) : (
                                        <>
                                          選択してください
                                        </>
                                      )
                                    }
                                  </>
                                )
                              }
                            </div>
                          </div>
                          <FontAwesomeIcon icon={['fas', 'arrow-right']} />
                        </SortableItem>
                      </div>
                    ))
                  }
                  <button
                    type="button"
                    style={{
                      fontSize: "20px",
                      padding: 0,
                      color: "#039831",
                    }}
                    onClick={addFlow}
                  >
                    <FontAwesomeIcon icon={['fas', 'plus-circle']} />
                  </button>
                </Grid>
              </SortableContext>
            </DndContext>
          )
        }
      </>
    )
  }

  const validationParams = () => {
    const tmpErrors: Errors = initErrors
    let errorFlg = false

    const ApproverIds = flow.map((v) => v.id).filter((v) => v)
    if (selectRequestMembers.some((v) => ApproverIds.includes(v.id))) {
      const diffMembers = selectRequestMembers.filter((v) => ApproverIds.includes(v.id))
      setApproverMembers(`${diffMembers.map((v) => v.name).join(",")}`)
    } else {
      setApproverMembers("")
    }
    const duplicationIds = Array.from(
      new Set(flow.map((v) => v.id).filter((v) => v).filter((v, _i, self) => (self.indexOf(v) !== self.lastIndexOf(v)))),
    )
    if (duplicationIds.length) {
      const duplicationMembers = modalState.memberList.filter((v) => duplicationIds.includes(v.id))
      tmpErrors.flow.push(`承認ユーザーが重複しています%br%対象ユーザー:${duplicationMembers.map((v) => v.name).join(",")}`)
      errorFlg = true
    }
    if (isFreeBuild && flow.filter((v) => !v.id).length) {
      tmpErrors.flow.push("承認ユーザーを選択してください")
      errorFlg = true
    }
    if (!isFreeBuild && flow.filter((v) => !!v.id).length === 0) {
      tmpErrors.flow.push("承認ユーザーは１名以上選択してください")
      errorFlg = true
    }
    if (errorFlg) {
      setErrors({
        name: tmpErrors.name,
        flow: tmpErrors.flow,
        member: tmpErrors.member,
      })
      return
    }
    setErrors(initErrors)
  }

  const checkOtherAssignedMember = () => {
    if (selectRequestMembers.filter((v) => v.id).some((v) => modalState.alreadyAssignedMemberIds.includes(v.id))) {
      setOtherAssignedMembers(selectRequestMembers.filter((v) => modalState.alreadyAssignedMemberIds.includes(v.id)))
    } else {
      setOtherAssignedMembers([])
    }
  }

  useEffect(() => {
    if (!modalState) return
    const { targetData } = modalState
    setGroupName(targetData?.name || initialInput.name)
    setSelectRequestMembers(targetData?.request_members || [])
    setSelectViewableMembers(targetData?.viewable_members || [])
    setErrors(initErrors)
  }, [modalState])

  useEffect(() => {
    setDisplayModal(modalState.display)
    setFlow(sortBy(modalState.targetData?.approve_members.map((v, i) => ({ ...v, item_id: i })), "priority").reverse())
    setOtherAssignedMembers([])
    setSelectTeamId(modalState.teamList[0]?.id || 0)
    setIsFreeBuild(true)
    closeApproverList()
  }, [modalState.display])

  useEffect(() => {
    validationParams()
    checkOtherAssignedMember()
  }, [isFreeBuild, flow, selectRequestMembers])

  return (
    <>
      <Modal
        title={modalState.type === "create" ? "承認グループの作成" : "承認グループの編集"}
        show={displayModal}
        wrapperClass="approver_setting_modal w750"
        closeCallback={clear}
        closeable
      >
        <>
          <div className="description">
            <div>
              承認グループの詳細を設定します。
            </div>
          </div>
          <div className="item">
            <div className="title flex">
              <div className="label red">必須</div>
              承認グループ名
            </div>
            <div className="detail row_1">
              <input
                type="text"
                placeholder="承認グループの名称を入力してください"
                onChange={(event) => setGroupName(event.target.value)}
                onBlur={(event) => replaceAndStripName(event)}
                value={groupName}
              />
            </div>
            {
              errors.name && errors.name.map((error) => (
                <div
                  className="error"
                  key={error}
                >
                  <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} style={{ fontSize: "20px" }} />
                  {/* eslint-disable-next-line react/no-danger */}
                  <div dangerouslySetInnerHTML={{ __html: error.replace('%br%', '<br />') }} />
                </div>
              ))
            }
          </div>
          <div className="item">
            <div className="title">
              承認フロータイプの選択
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                marginBottom: "8px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  cursor: "pointer",
                  color: isFreeBuild ? "#008800" : "#666666",
                }}
                onClick={() => changeFlowType(true)}
                role="none"
              >
                <FontAwesomeIcon
                  icon={[isFreeBuild ? 'fas' : 'far', isFreeBuild ? 'check-circle' : 'circle']}
                />
                自由に作成する
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  cursor: "pointer",
                  color: !isFreeBuild ? "#008800" : "#666666",
                }}
                onClick={() => changeFlowType(false)}
                role="none"
              >
                <FontAwesomeIcon
                  icon={[!isFreeBuild ? 'fas' : 'far', !isFreeBuild ? 'check-circle' : 'circle']}
                />
                チームから選択する
              </div>
            </div>
          </div>
          <div className="item">
            <div className="title">
              承認フロー
            </div>
            {
              !isFreeBuild && (
                <>
                  <div
                    style={{
                      padding: "4px",
                      fontSize: "10px",
                      color: "#FF0000",
                    }}
                  >
                    ※承認ユーザーを設定しない場合、未設定のフローは省略されて登録します
                  </div>
                  <div
                    style={{
                      marginBottom: "8px",
                    }}
                  >
                    <select
                      style={{
                        width: "100%",
                        border: "solid 1px #999999",
                        borderRadius: "4px",
                        padding: "4px 24px 4px 4px",
                        fontWeight: "normal",
                        appearance: "auto",
                        backgroundColor: !isFreeBuild ? "transparent" : "#EEEEEE",
                        cursor: !isFreeBuild ? "initial" : "not-allowed",
                      }}
                      defaultValue={selectTeamId}
                      disabled={isFreeBuild}
                      onChange={(e) => selectTargetTeamId(Number(e.target.value))}
                    >
                      {
                        sortedTeamList.filter((v) => !!v.parent_team_id).map((v) => (
                          <option value={v.id}>
                            {buildFullPathName(v.id)}
                          </option>
                        ))
                      }
                    </select>
                  </div>
                </>
              )
            }
            <fieldset className="approver_area_wrapper">
              <div className="approver_area">
                {buildApprovalFlow(flow)}
              </div>
            </fieldset>
            {
              errors.flow && errors.flow.map((error) => (
                <div
                  className="error"
                  key={error}
                >
                  <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} style={{ fontSize: "20px" }} />
                  {/* eslint-disable-next-line react/no-danger */}
                  <div dangerouslySetInnerHTML={{ __html: error.replace('%br%', '<br />') }} />
                </div>
              ))
            }
          </div>
          <div className="item">
            <div className="title">
              申請ユーザー
            </div>
            <fieldset className="member_area_wrapper">
              <div className="member_area">
                {
                  selectRequestMembers.map((value) => (
                    <div
                      className="member_block"
                      key={`condition_${value.id}`}
                    >
                      <img
                        alt="profile"
                        src={value.profile_image || `/static/images/profile/${(Number(value.id) || 0) % 12}.jpeg`}
                        className="profile_icon"
                      />
                      {value.name}
                      <span
                        style={{
                          cursor: "pointer",
                        }}
                        onClick={() => delRequestMember(value.id)}
                        role="none"
                      >
                        <FontAwesomeIcon icon={['fas', 'times']} />
                      </span>
                    </div>
                  ))
                }
                <AddMemberModule
                  memberIdList={selectRequestMembers.map((v) => v.id)}
                  callbackMembers={(val) => setRequestMember([...modalState.memberList.filter((v) => val.map((w) => w.id).includes(v.id))])}
                  memberList={modalState.memberList.map((v) => ({ id: v.id, name: v.name, profile_image: v.profile_image }))}
                />
              </div>
            </fieldset>
            {
              !!otherAssignedMembers.length && (
                <div className="info_area">
                  <FontAwesomeIcon icon={['far', 'lightbulb']} style={{ fontSize: "20px", color: "#babd24", margin: "0 4px" }} />
                  <div>
                    <div>
                      対象の申請ユーザーを他の承認グループからこの承認グループに移動します
                    </div>
                    <div>
                      {`対象申請者:${otherAssignedMembers.map((v) => v.name).join(",")}`}
                    </div>
                  </div>
                </div>
              )
            }
            {
              !!approverMembers && (
                <div className="info_area">
                  <FontAwesomeIcon icon={['far', 'lightbulb']} style={{ fontSize: "20px", color: "#babd24", margin: "0 4px" }} />
                  <div>
                    <div>
                      承認ユーザーが対象申請ユーザーに設定されています
                    </div>
                    <div>
                      ご注意ください
                    </div>
                    <div>
                      {`対象申請者:${approverMembers}`}
                    </div>
                  </div>
                </div>
              )
            }
            {
              errors.member && errors.member.map((error) => (
                <div
                  className="error"
                  key={error}
                >
                  <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} style={{ fontSize: "20px" }} />
                  {/* eslint-disable-next-line react/no-danger */}
                  <div dangerouslySetInnerHTML={{ __html: error.replace('%br%', '<br />') }} />
                </div>
              ))
            }
          </div>
          <div className="item">
            <div className="title">
              閲覧ユーザー
            </div>
            <fieldset className="member_area_wrapper">
              <div className="member_area">
                {
                  selectViewableMembers.map((value) => (
                    <div
                      className="member_block"
                      key={`condition_${value.id}`}
                    >
                      <img
                        alt="profile"
                        src={value.profile_image || `/static/images/profile/${(Number(value.id) || 0) % 12}.jpeg`}
                        className="profile_icon"
                      />
                      {value.name}
                      <span
                        style={{
                          cursor: "pointer",
                        }}
                        onClick={() => delViewableMember(value.id)}
                        role="none"
                      >
                        <FontAwesomeIcon icon={['fas', 'times']} />
                      </span>
                    </div>
                  ))
                }
                <AddMemberModule
                  memberIdList={selectViewableMembers.map((v) => v.id)}
                  callbackMembers={(val) => setViewableMember([...modalState.memberList.filter((v) => val.map((w) => w.id).includes(v.id))])}
                  memberList={modalState.memberList.map((v) => ({ id: v.id, name: v.name, profile_image: v.profile_image }))}
                />
              </div>
            </fieldset>
          </div>
        </>
        <>
          <button type="button" className="button cancel" onClick={clear}>
            {t('form.component.cancel')}
          </button>
          <button
            type="button"
            className="button"
            onClick={saveApprovalGroup}
            disabled={!!errors.name.length || !!errors.flow.length || !!errors.member.length}
          >
            { modalState.type === 'create' ? "作成する" : "編集する" }
          </button>
        </>
      </Modal>
    </>
  )
}

export default ApproverSettingModal
